/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { useAccessibleDropdown, useUniqueId } from '@squiz/dxp-console-support';
import clsx from 'clsx';
import React, { useCallback, useEffect, useMemo } from 'react';
import { DXP_ACTIVE_CLASS } from '../../Constants/Design';
import { Button } from '../Button';
import { dropdownDefaults, DropdownMenuProps } from './Dropdown.props';
import { DropdownSection } from './elements/DropdownSection';

/**
 * SDS Dropdown component.
 */
export const Dropdown = (props: DropdownMenuProps): JSX.Element => {
  const {
    buttonProps,
    classes,
    customHeading,
    dropdownPosition,
    heading,
    onClose,
    onOpen,
    overlayStateHandler,
    sections,
    toggleOverlay,
    triggerClose,
    ...rest
  } = {
    ...dropdownDefaults,
    ...props,
  };

  const uniqueId = useUniqueId();
  const wrapperId = `sds-dropdown-wrapper--${uniqueId}`;
  const menuId = `sds-dropdown--${uniqueId}`;
  const buttonId = `sds-dropdown-button--${uniqueId}`;

  const menuItemCount = useMemo<number>(() => {
    let count = 0;
    sections.forEach((section) => {
      if (!section.customContent) {
        section.items.forEach(() => {
          count++;
        });
      }
    });
    return count;
  }, [sections]);

  const { accessibleDropdownButtonProps, accessibleDropdownItemProps, dropdownOpen, setDropdownOpen } =
    useAccessibleDropdown({
      dropdownSelector: `#${wrapperId}`,
      itemCount: menuItemCount,
      onClose,
      onOpen,
    });

  const wrapperClass = clsx(`sds-dropdown-wrapper`, classes);
  const dropdownClass = clsx(`sds-dropdown`, `sds-dropdown--${dropdownPosition}`, {
    [DXP_ACTIVE_CLASS]: dropdownOpen,
  });

  // If applicable, update the overlay state based on the dropdown state
  useEffect(() => {
    if (toggleOverlay && overlayStateHandler) {
      const { updateActiveOverlay } = overlayStateHandler.getState();
      updateActiveOverlay(dropdownOpen ? toggleOverlay : null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dropdownOpen, overlayStateHandler]);

  const closeHandler = useCallback((): void => {
    setDropdownOpen(false);
    onClose && onClose();
  }, [onClose, setDropdownOpen]);

  const getHeading = (): JSX.Element => {
    if (customHeading) {
      return <div className="sds-dropdown-heading--custom">{customHeading}</div>;
    }

    if (heading) {
      return (
        <div className="sds-dropdown-heading">
          <span>{heading}</span>
        </div>
      );
    }

    return <></>;
  };

  const getSections = (): Array<JSX.Element> => {
    let itemIndex = 0;
    return sections.map((section, sectionIndex): JSX.Element => {
      if (sectionIndex > 0) {
        itemIndex += sections[sectionIndex - 1].items.length;
      }
      return (
        <React.Fragment key={section.key}>
          <DropdownSection
            accessibleDropdownItemProps={accessibleDropdownItemProps}
            closeDropdown={closeHandler}
            dropdownSection={section}
            firstItemIndex={itemIndex}
          />
        </React.Fragment>
      );
    });
  };

  useEffect(() => {
    if (triggerClose && dropdownOpen) {
      closeHandler();
    }
  }, [closeHandler, dropdownOpen, triggerClose]);

  return (
    <div className={wrapperClass} id={wrapperId} {...rest}>
      <Button
        {...buttonProps}
        {...accessibleDropdownButtonProps}
        aria-controls={menuId}
        aria-label={buttonProps.label}
        id={buttonId}
      />
      <div aria-labelledby={buttonId} className={dropdownClass} id={menuId} role="menu">
        {dropdownOpen && (
          <>
            {getHeading()}
            <div>{getSections()}</div>
          </>
        )}
      </div>
    </div>
  );
};
